import { MouseEventHandler } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { Input, InputField, ProgressBar, Icon, Button, Link } from '@ets/design-system';

import formatBytes from 'utils/formatBytes';

import { Modals } from '_common/modals/ModalsSliceTypes';
import { useSelector } from '_common/hooks';

import { Textarea, type TextareaProps } from '_common/lib/components';

import styles from '../CheckInModal.module.scss';

type UploadErrorProps = {
  reset: MouseEventHandler<HTMLSpanElement>;
  name: string;
  editing?: boolean;
  toggleEditingName: MouseEventHandler<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>;
  onInputChanged: TextareaProps['onChange'];
  comment?: string;
  modal: Modals;
};

const UploadErrors = ({
  reset,
  editing,
  onInputChanged,
  name,
  toggleEditingName,
  comment,
  modal,
}: UploadErrorProps) => {
  const intl = useIntl();

  // redux
  const loading = useSelector((state) => state.modals[modal].loading);
  const file = useSelector((state) => state.modals[modal].file);
  const errors = useSelector((state) => state.modals[modal].errors);
  const uploadPercentage = useSelector((state) => state.modals[modal].uploadPercentage);

  const { size } = file;
  return (
    <div>
      <div className={styles.warning}>
        <span className={styles.clearButton} onClick={reset}>
          <Icon icon="xmark" size="3" testId="close" />
        </span>
        <Icon icon="file-lines" size="12" testId="document" color="primary" />
        {editing ? (
          <div>
            <Input
              name="name"
              onChange={onInputChanged}
              value={name}
              size="m"
              placeholder=""
              testId="name"
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '0.5rem',
              }}
            >
              <Button size="m" variant="primary" onClick={toggleEditingName} testId="save">
                <FormattedMessage id="global.save" />
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ display: 'flex' }}>
                <span className={styles.value}>{name}</span>
                <span className={styles.append}>{`(${formatBytes(size)})`}</span>
              </div>
              {!loading && (
                <Link size="s" variant="primary" onClick={toggleEditingName} testId="edit-name">
                  <FormattedMessage id="storage.modals.checkIn.click" />
                </Link>
              )}
              {loading && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '1.5rem',
                  }}
                >
                  <ProgressBar
                    variant="primary-light"
                    progress={uploadPercentage}
                    size="m"
                    testId="check-in-modal-uploading-with-error"
                  />
                </div>
              )}
            </div>
          </div>
        )}
        <div className={styles.error}>
          <span>
            <FormattedMessage id="storage.modals.checkIn.errorDesc" />
          </span>
          {errors.name && (
            <div style={{ marginTop: '0.5rem' }}>
              <FormattedMessage id="storage.modals.checkIn.errorName" />
            </div>
          )}
          {errors.mime && (
            <div style={{ marginTop: '0.5rem' }}>
              <FormattedMessage id="storage.modals.checkIn.errorMime" />
            </div>
          )}
          {errors.extension === true && (
            <div style={{ marginTop: '0.5rem' }}>
              <FormattedMessage id="storage.modals.checkIn.errorExt" />
            </div>
          )}
        </div>
      </div>
      <div className={styles.comment}>
        <InputField
          label={intl.formatMessage({
            id: 'storage.modals.checkIn.leaveComment',
          })}
          size="l"
          testId="comment"
        >
          <Textarea
            plainText
            size="m"
            type="text"
            name="comment"
            placeholder={intl.formatMessage({
              id: 'storage.modals.checkIn.leaveCommentPlaceholder',
            })}
            initialContent={comment}
            onChange={onInputChanged}
            testId="leave-a-comment-textarea"
          />
        </InputField>
      </div>
    </div>
  );
};

export default UploadErrors;
